import { createAsyncThunk } from "@reduxjs/toolkit";
import { instance } from "src/services/api-client";
import { NotificationService } from "src/helpers/notifications";
import { InquirySettingsValues } from "src/page-components/cabinet/my-inquiries/InquirySettings/types";
import {
  errorResponsesTrns,
  pendingResponsesTrns,
  successfulResponsesTrns,
} from "src/translate/responses";
import { IInquiry } from "src/@types/inquiries";
import { IResponseWithPagination } from "src/@types/api";
import { CloneStatus } from "src/@types/clones";

export const ITEMS_PER_PAGE = 10;

export const MY_INQUIRIES_SLICE_NAME = "myInquiries";
interface Params {
  sorting: string;
  page?: number;
}

export const getMyInquiriesAsync = createAsyncThunk(
  `${MY_INQUIRIES_SLICE_NAME}/fetchMyInquiries`,
  async ({ sorting = "", page = 1 }: Params, { rejectWithValue }) => {
    try {
      const sortingQueryStr = sorting ? `&status=${sorting}` : "";

      const { data } = await instance.get<IResponseWithPagination<IInquiry>>(
        `/inquiries/my-inquiries/?page=${page}${sortingQueryStr}&page_size=${ITEMS_PER_PAGE}`
      );

      return data;
    } catch ({ response }) {
      const errorText = response?.data?.error || errorResponsesTrns.myInquiries;

      NotificationService.error(errorText);

      return rejectWithValue(errorText);
    }
  }
);

interface CreateInquiryParams {
  values: InquirySettingsValues;
  onSuccess?: (inquiry: IInquiry) => void;
}
export const createInquiryAsync = createAsyncThunk(
  `${MY_INQUIRIES_SLICE_NAME}/createInquiry`,
  async ({ values, onSuccess }: CreateInquiryParams, { rejectWithValue }) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.createInquiry
    );

    try {
      const { data } = await instance.post<IInquiry>("/inquiries/", values);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.createInquiry
      );

      onSuccess?.(data);
    } catch ({ response }) {
      const errorData = response?.data;

      if (errorData) {
        Object.entries(errorData).forEach(([key, messages]) => {
          const message = Array.isArray(messages)
            ? messages.join(", ")
            : messages;
          NotificationService.updateToError(toastId, `${key} - ${message}`);
        });
      } else {
        const errorText = response?.data?.error;
        NotificationService.updateToError(
          toastId,
          errorText || errorResponsesTrns.createInquiry
        );
        return rejectWithValue(errorText);
      }

      return rejectWithValue(errorData);
      // =============
      // const errorText = response?.data?.error;
      // NotificationService.updateToError(
      //   toastId,
      //   errorText || errorResponsesTrns.createInquiry
      // );

      // return rejectWithValue(errorText);
    }
  }
);

interface EditInquiryParams {
  values: InquirySettingsValues;
  id: string;
  onSuccess?: () => void;
}
export const editInquiryAsync = createAsyncThunk(
  `${MY_INQUIRIES_SLICE_NAME}/editInquiry`,
  async (
    { values, id, onSuccess = () => {} }: EditInquiryParams,
    { rejectWithValue }
  ) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.editInquiry
    );

    try {
      await instance.patch<{ id: number }>(`/inquiries/${id}/`, values);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.editInquiry
      );

      onSuccess();
    } catch ({ response }) {
      const errorData = response?.data;

      if (errorData) {
        Object.entries(errorData).forEach(([key, messages]) => {
          const message = Array.isArray(messages)
            ? messages.join(", ")
            : messages;
          NotificationService.updateToError(toastId, `${key} - ${message}`);
        });
      } else {
        const errorText = response?.data?.error;
        NotificationService.updateToError(
          toastId,
          errorText || errorResponsesTrns.createInquiry
        );
        return rejectWithValue(errorText);
      }

      return rejectWithValue(errorData);
      // =====
      // const errorText = response?.data?.error;

      // NotificationService.updateToError(
      //   toastId,
      //   errorText || errorResponsesTrns.editInquiry
      // );

      // return rejectWithValue(errorText);
    }
  }
);

export const deleteInquiryAsync = createAsyncThunk(
  `${MY_INQUIRIES_SLICE_NAME}/deleteInquiry`,
  async (id: number, { rejectWithValue }) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.deleteInquiry
    );

    try {
      await instance.delete(`/inquiries/${id}/`);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.deleteInquiry
      );
      return id;
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        toastId,
        errorText || errorResponsesTrns.deleteInquiry
      );

      return rejectWithValue(errorText);
    }
  }
);

interface ChangeInquiryStatusParams {
  id: number;
  status: CloneStatus;
}

export const changeInquiryStatusAsync = createAsyncThunk(
  `${MY_INQUIRIES_SLICE_NAME}/changeInquiryStatus`,
  async ({ id, status }: ChangeInquiryStatusParams, { rejectWithValue }) => {
    try {
      await instance.patch(`/inquiries/${id}/`, { status });

      return { id, status };
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        errorText || errorResponsesTrns.default
      );

      return rejectWithValue(errorText);
    }
  }
);
